import { isDate } from 'date-fns'
import {
  all,
  call,
  delay,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects'
import {
  databaseForEventsIsEmpty,
  dataForEventsIsAccepted,
  dataForEventsIsRejected,
  IDatabaseForEventsEmpty,
  IDataForEventsIsAccepted,
} from '../../actions/bookingCalendar/event/requestDataForEvents'
import { callFetch } from '../../actions/fetch'
import { fireToastWarning } from '../../actions/toasts/fireToast'
import { database, localStorage } from '../../database'
import i18n from '../../i18n'
import { fetchEvents } from '../../models/Event'
import mapEventFromDb from '../../models/Event/mappers/mapEventFromDb'
import { addOffsetDate } from '../../models/License/mappers/mapResponseToLicense'
import {
  CalendarActions,
  EventActions,
} from '../../reducers/bookingCalendar/actionNames'
import { getCalendarId } from '../../reducers/bookingCalendar/selectors/calendar'
import { getIsFullyLoaded } from '../../reducers/bookingCalendar/selectors/dates'
import fetchActionsName, { ActionNames } from '../../utils/FetchActionsName'
import takeLatestWithSignOff from '../../utils/Saga/TakeLatestUntilSignoff'
import { getUserId, getWorkerId } from '../../reducers/userAccount/selectors/userAccount'

function* loadFromDatabase(action) {
  try {
    yield race({
      done: take(CalendarActions.CalendarsFetched),
      fail: delay(1000),
    })

    const isFullyLoaded = yield select(getIsFullyLoaded)

    if (isFullyLoaded) {
      yield put(dataForEventsIsAccepted([], false, true))
      return
    }

    // const isLoading = yield select(getEventsAreLoading)
    // if (isLoading) {
    //   return
    // }

    const calendarId = yield select(getCalendarId)
    if (undefined === calendarId) {
      yield delay(1000)
      yield put(action)
    }
    const userId = yield select(getUserId)
    const workerId = yield select(getWorkerId)

    const eventsInDB = yield database.events.where({ calendarId, userId: workerId || userId }).toArray()

    if (eventsInDB.length > 0) {
      const events = eventsInDB.map(mapEventFromDb)

      yield put(dataForEventsIsAccepted(events, false, true))
    } else {
      yield put(databaseForEventsIsEmpty())
    }
  } catch (e) {
    yield database.events.clear()
    yield put(databaseForEventsIsEmpty())
    yield fireToastWarning(i18n.t('error_server_response_4'))
  }
}

export function* loadFromApi(action: IDatabaseForEventsEmpty) {
  if (action.onlyToBase) {
    yield put({ loading: true, type: EventActions.ToggleFallbackLoading })
  }

  try {
    const actionNames = fetchActionsName(ActionNames.LoadEvents)
    const [{ calendarId, service }] = yield all([
      take(actionNames.output),
      put(callFetch(actionNames.input, actionNames.output, fetchEvents)),
    ])

    if (!calendarId) {
      yield delay(1000)
      yield put(action)
    }

    const events = yield call(service, calendarId, action.lastModifyDate)

    const d: number = isDate(action.lastModifyDate)
      ? Math.floor(action.lastModifyDate.getTime() / 1000)
      : 0

    if (d > 0) {
      const { UserDriver: Driver, Names } = localStorage
      const { UserAccountStorage } = Names
      Driver.setItem(
        UserAccountStorage.LastSync,
        action.lastModifyDate.getTime().toString(),
      )
    }

    if (action.onlyToBase) {
      yield put({ loading: false, type: EventActions.ToggleFallbackLoading })
    }
    yield put(dataForEventsIsAccepted(events, true, !d))
  } catch (e) {
    yield put(
      dataForEventsIsRejected(
        e.message + `/n l:${action.lastModifyDate} /n` + e.stack,
      ),
    )
    yield put({ loading: false, type: EventActions.ToggleFallbackLoading })
  }
}

export function* putToIndexedDB(action: IDataForEventsIsAccepted) {
  if (!action.putToDB) {
    return
  }

  try {
    const calendarId = yield select(getCalendarId)
    const events = [...action.events]
    const userId = yield select(getUserId)
    const workerId = yield select(getWorkerId)


    yield database.events.bulkPut(
      events.map((event): any => {
        return {
          ...event,
          bookingPrice: null,
          calendarId,
          userId: workerId || userId,
          dateToPay: event.dateToPay
            ? addOffsetDate(event.dateToPay).getTime()
            : undefined,
          dateToPayAfterStart: event.dateToPayAfterStart
            ? addOffsetDate(event.dateToPayAfterStart).getTime()
            : undefined,
          deposit: null,
          finishDate: addOffsetDate(event.finishDate).getTime(),
          localTax: null,
          paymentOnPlace: null,
          paymentOnPlaceDate: event.paymentOnPlaceDate
            ? addOffsetDate(event.paymentOnPlaceDate).getTime()
            : undefined,
          price: null,
          pricePerDay: null,
          provision: null,
          provisionDate: event.provisionDate
            ? addOffsetDate(event.provisionDate).getTime()
            : undefined,
          savedColor: event.customColor
            ? event.customColor.rgb().toString()
            : null,
          startDate: addOffsetDate(event.startDate).getTime(),
        }
      }),
    )

    yield put({ type: 'Events added to db' })
  } catch (e) {
    // tslint:disable-next-line
    console.error('loadEvents:putToIndexedDB', e.message)
  }
}

//
// function* clearRedux() {
//   yield put(dataForEventsIsAccepted([]))
// }

export default function*() {
  yield takeLatestWithSignOff(
    EventActions.RequestDataForEvents,
    loadFromDatabase,
  )
  yield takeLatestWithSignOff(
    EventActions.DatabaseForEventsIsEmpty,
    loadFromApi,
  )
  yield takeLatest(EventActions.DataForEventsAccepted, putToIndexedDB)
}
